import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Target, Zap, TrendingUp } from 'lucide-react';

const ProblemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-24 bg-white" id="sobre">
      <div className="container mx-auto px-6 lg:px-20">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Por que sua landing page precisa ser{' '}
            <span className="text-blue-600">diferente</span>?
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            A maioria dos sites são apenas "cartões de visita digitais". Aqui, cada elemento é pensado estrategicamente para gerar ação: contato, agendamento, venda.
          </p>
        </motion.div>

        {/* Key Differences */}
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <motion.article
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-center"
          >
            <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Target className="text-blue-600" size={36} aria-hidden="true" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Objetivo Claro</h3>
            <p className="text-gray-600 leading-relaxed">
              Seu visitante não se perde. A página guia ele naturalmente até o contato ou ação desejada.
            </p>
          </motion.article>

          <motion.article
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center"
          >
            <div className="w-20 h-20 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Zap className="text-green-600" size={36} aria-hidden="true" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Rápido e Direto</h3>
            <p className="text-gray-600 leading-relaxed">
              Informações essenciais, sem enrolação. Seu cliente entende sua oferta em segundos.
            </p>
          </motion.article>

          <motion.article
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center"
          >
            <div className="w-20 h-20 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <TrendingUp className="text-purple-600" size={36} aria-hidden="true" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Otimizado para Conversão</h3>
            <p className="text-gray-600 leading-relaxed">
              Cada botão, texto e imagem têm um propósito: transformar visita em resultado real.
            </p>
          </motion.article>
        </div>

        {/* Bottom Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-20 text-center max-w-3xl mx-auto"
        >
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-2xl px-8 py-8">
            <p className="text-2xl font-bold text-gray-900 mb-3">
              Não é sobre ter um site. É sobre ter resultados.
            </p>
            <p className="text-lg text-gray-600">
              E é exatamente isso que eu entrego para você.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;
